<template>
  <div class="lazy-load-movie-select">
    <el-select
      ref="selectRef"
      v-load-more="loadMoreFn"
      v-model="innerValue"
      clearable
      filterable
      remote
      :remote-method="debounceSearch"
      :placeholder="placeholder"
      :style="style"
      :loading="isSearching"
      loadingText="ロード中"
      noMatchText="データなし"
      noDataText="データなし"
      @focus="isFocused = true"
      @blur="resetList"
      @change="onChange"
      >
      <el-option
        v-for="item in options"
        :key="item.id"
        :label="optionLabel(item)"
        :value="item.id"
        ></el-option>
    </el-select>
  </div>
</template>
<script setup lang="ts">
import {
  computed, ref, watch, getCurrentInstance, nextTick, withDefaults, defineEmits,
  ComponentInternalInstance,
} from 'vue';

import _ from 'lodash';
import dayjs from 'dayjs';

// interface
type listTypes = {
  id: number;
  name: string;
  // 公开日（YYYY-MM-DD），/dp/movieList 返回；showOpenDate 时显示在作品名后
  openDate?: string;
}[]
// props
const props = withDefaults(
  defineProps<{
    modelValue: string,
    list: listTypes,
    placeholder: string | undefined,
    style: any,
    fetchOptions: any,
    companyId: string,
    dates: string[],
    isFetch: boolean,
    showOpenDate?: boolean,
  }>(),
  {
    modelValue: '',
    list: () => [],
    placeholder: undefined,
    style: { width: '240px' },
    fetchOptions: () => {},
    companyId: '',
    dates: () => [],
    isFetch: true,
    showOpenDate: false,
  }
)
// emit
const emit = defineEmits(['update:modelValue', 'change']);
// data
const innerValue = ref<string>(props.modelValue);
const isSearching = ref<boolean>(false);
const isFocused = ref<boolean>(false);
const searchQuery = ref<string>('');
const options = ref<listTypes>(props.list);
const loadMoreLoading = ref<boolean>(false);
// const totalItems = ref<number>(0);
const isCanFetch = ref<boolean>(true);
const currentPage = ref<number>(1);
// method

// 候选项文字：showOpenDate 时在作品名后附公开日，如「作品名（2026/10/16 公開）」；无公开日则只显示作品名
const optionLabel = (item: listTypes[number]) => {
  if (!props.showOpenDate || !item.openDate) return item.name;
  return `${item.name}（${dayjs(item.openDate).format('YYYY/MM/DD')} 公開）`;
}

// 每页条数：与父组件首屏请求（page=0）的 pageSize 保持一致
const PAGE_SIZE = 50;

// 把当前选中项并入候选列表：重置候选时不能丢掉选中项，否则 el-select 只能显示原始 id
const withSelected = (list: listTypes) => {
  const selected = options.value.find((item) => item.id === (innerValue.value as any));
  return _.uniqBy(selected ? [...list, selected] : list, 'id');
}

// 恢复为父组件传入的首屏列表，并重置分页状态（下一次加载更多从第 1 页开始）
const restoreBaseList = () => {
  options.value = withSelected(props.list);
  currentPage.value = 1;
  isCanFetch.value = props.list.length >= PAGE_SIZE;
}

// list懒加载
const loadMoreFn = () => {
  if (isSearching.value) return;
  if (loadMoreLoading.value || (!isCanFetch.value)) return;
  loadMoreLoading.value = true;
  props.fetchOptions({
    ...searchQuery.value ? {
      name: searchQuery.value,
    } : {},
    ...props.dates && props.dates.length > 0 ? {
      startTime: dayjs(props.dates[0]).startOf("day").valueOf(),
      endTime: dayjs(props.dates[1]).add(1, "day").startOf("day").valueOf(),
    } : {},
    page: currentPage.value,
    pageSize: PAGE_SIZE,
    companyId: props.companyId,
  }).then((response: any) => {
    // 按 id 去重：防止分页边界上的同一作品重复出现（重复 key 会导致下拉渲染异常）
    options.value = _.uniqBy([...options.value, ...response], 'id');
    // 不足一页说明已到末尾，不再请求
    isCanFetch.value = response.length >= PAGE_SIZE;
    currentPage.value++;
  }).finally(() => {
    // 请求失败时也要解除加载中状态，否则之后永远无法再加载更多
    loadMoreLoading.value = false;
  });
}
// 关键字搜索
const debounceSearch = _.debounce(async (query) => {
  if (!isFocused.value) return;
  if (!query) {
    // 清空关键字：从搜索结果恢复为首屏列表
    if (searchQuery.value || options.value.length === 0) {
      searchQuery.value = '';
      restoreBaseList();
    }
    return;
  }
  isSearching.value = true;
  searchQuery.value = query;
  currentPage.value = 1;
  // 搜索已选择的数据
  await props.fetchOptions({
    ...query ? {
      name: query,
    } : {},
    ...props.dates && props.dates.length > 0 ? {
      startTime: dayjs(props.dates[0]).startOf("day").valueOf(),
      endTime: dayjs(props.dates[1]).add(1, "day").startOf("day").valueOf(),
    } : {},
    page: 0,
    pageSize: PAGE_SIZE,
    companyId: props.companyId,
  }).then((response: any) => {
    // 响应返回前关键字已变化（或已失焦清空）时丢弃旧结果
    if (searchQuery.value !== query) return;
    options.value = withSelected(response);
    // 每次新搜索都重新判断能否继续翻页，不沿用上一次列表的「已到末尾」状态
    isCanFetch.value = response.length >= PAGE_SIZE;
  }).finally(() => {
    isSearching.value = false;
  })
}, 300);

// 取消焦点后：若处于搜索状态，恢复为首屏列表（保留选中项），避免后续加载更多把未过滤的数据拼到搜索结果后面
const resetList = () => {
  isFocused.value = false;
  if (searchQuery.value) {
    searchQuery.value = '';
    restoreBaseList();
  }
}

const onChange = (val: any) => {
  emit('change', val);
}
watch(() => props.modelValue, (newVal) => {
  innerValue.value = newVal;
});
// 父组件重新获取首屏列表（如更换期间后再次検索）时，分页状态从头开始，避免跳过中间页
watch(() => props.list, (newVal) => {
  if (newVal.length > 0) {
    searchQuery.value = '';
    options.value = _.uniqBy(newVal, 'id');
    currentPage.value = 1;
    isCanFetch.value = newVal.length >= PAGE_SIZE;
  }
})
watch(innerValue, (newVal) => {
  emit('update:modelValue', newVal);
});
watch(() => props.isFetch, (newVal) => {
  isCanFetch.value = newVal;
})
</script>
<style scoped>
.lazy-load-movie-select {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.lazy-load-movie-select :deep(.el-select),
.lazy-load-movie-select :deep(.select-trigger),
.lazy-load-movie-select :deep(.el-input) {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0;
  box-sizing: border-box;
}

/* 预留 suffix 宽度：hover 时清空图标与下拉箭头切换，避免撑宽 select */
.lazy-load-movie-select :deep(.el-input__wrapper) {
  width: 100% !important;
  box-sizing: border-box;
}

.lazy-load-movie-select :deep(.el-input__inner),
.lazy-load-movie-select :deep(input) {
  width: 100% !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lazy-load-movie-select :deep(.el-input__suffix) {
  width: 32px;
  flex: 0 0 32px;
}

.lazy-load-movie-select :deep(.el-input__suffix-inner) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
}

.lazy-load-movie-select :deep(.el-select__caret),
.lazy-load-movie-select :deep(.el-input__suffix .el-icon) {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
</style>
